import {format, parseISO} from "date-fns";

/**
 * Formats a date string from any format to YYYY-MM-DD
 * required by HTML date inputs
 */
export const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';

    try {
        // Parse the date string to a Date object
        const date = createLocalDate(dateString);

        // Check if date is valid
        if (isNaN(date.getTime())) {
            console.warn('Invalid date:', dateString);
            return '';
        }

        // Format to YYYY-MM-DD (required by HTML date inputs)
        return date.toISOString().split('T')[0];
    } catch (error) {
        console.error('Error formatting date:', error);
        return '';
    }
};

export const formatDateToString = (date: Date | null | undefined): string => {
    // Handle null, undefined, and invalid dates
    if (!date || isNaN(date.getTime())) {
        return '';
    }

    // Extract the year, month, and day directly from the Date object
    // to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};


export const createLocalDate = (dateString: string | Date | null | undefined): Date => {
    // Handle null/undefined cases
    if (!dateString) {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to midnight local time
        return today;
    }

    // If it's already a Date object, create a new date at midnight local time
    if (dateString instanceof Date) {
        const newDate = new Date(dateString);
        newDate.setHours(0, 0, 0, 0); // Set to midnight local time
        return newDate;
    }

    // For string dates, parse and set to midnight local time
    let date;
    const trimmedDateString = String(dateString).trim();

    // First, check if the input is already in YYYY-MM-DD format
    const isoRegex = /^(\d{4})-(\d{1,2})-(\d{1,2})(T.*)?$/;
    const isoMatch = trimmedDateString.match(isoRegex);

    if (isoMatch) {
        // If it matches the ISO format, extract parts and create date at midnight local time
        const year = parseInt(isoMatch[1]);
        const month = parseInt(isoMatch[2]) - 1; // JS months are 0-based
        const day = parseInt(isoMatch[3]);
        date = new Date(year, month, day, 0, 0, 0, 0); // Local midnight
    } else {
        // Try to parse other formats
        const slashRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/; // MM/DD/YYYY or DD/MM/YYYY
        const slashMatch = trimmedDateString.match(slashRegex);

        if (slashMatch) {
            // Determine if it's MM/DD/YYYY or DD/MM/YYYY
            // Assume MM/DD/YYYY if first part ≤ 12
            const isMMDDYYYY = parseInt(slashMatch[1]) <= 12;

            if (isMMDDYYYY) {
                date = new Date(
                    parseInt(slashMatch[3]), // Year
                    parseInt(slashMatch[1]) - 1, // Month
                    parseInt(slashMatch[2]), // Day
                    0, 0, 0, 0 // Midnight local time
                );
            } else {
                date = new Date(
                    parseInt(slashMatch[3]), // Year
                    parseInt(slashMatch[2]) - 1, // Month
                    parseInt(slashMatch[1]), // Day
                    0, 0, 0, 0 // Midnight local time
                );
            }
        } else {
            // Try to parse dash-separated or other formats
            const parts = trimmedDateString.split(/[\/\-\.]/);

            if (parts.length === 3) {
                // If first part is likely a year (length 4)
                if (parts[0].length === 4) {
                    // YYYY-MM-DD or YYYY/MM/DD
                    date = new Date(
                        parseInt(parts[0]),
                        parseInt(parts[1]) - 1,
                        parseInt(parts[2]),
                        0, 0, 0, 0 // Midnight local time
                    );
                }
                // For other formats, as a last resort, try the JavaScript Date parser
                else {
                    date = new Date(trimmedDateString);
                    // Set to midnight local time if valid
                    if (!isNaN(date.getTime())) {
                        date.setHours(0, 0, 0, 0);
                    }
                }
            } else {
                // As a last resort, try the JavaScript Date parser
                date = new Date(trimmedDateString);
                // Set to midnight local time if valid
                if (!isNaN(date.getTime())) {
                    date.setHours(0, 0, 0, 0);
                }
            }
        }
    }

    // If all parsing attempts failed, return today's date at midnight
    if (!date || isNaN(date.getTime())) {
        console.warn(`Invalid date format: ${dateString}, using current date instead`);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to midnight local time
        return today;
    }

    return date;
};

/**
 * Formats a date value (string or Date object) to a user-friendly display format
 * Example: "March 7, 2025"
 *
 * @param dateValue Date string or Date object to format
 * @param options Optional Intl.DateTimeFormatOptions to customize the format
 * @returns Formatted date string or 'N/A' if invalid
 */
export const formatDate = (
    dateValue: Date | string | null | undefined,
    options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }
): string => {
    if (!dateValue) return 'N/A';

    try {
        // Convert string to Date object if necessary
        const date = typeof dateValue === 'string' ? createLocalDate(dateValue) : dateValue;

        // Check if date is valid
        if (isNaN(date.getTime())) {
            console.warn('Invalid date value:', dateValue);
            return 'N/A';
        }

        // Format the date using Intl.DateTimeFormat for localization support
        return new Intl.DateTimeFormat('en-US', options).format(date);
    } catch (error) {
        console.error('Error formatting date for display:', error);
        return 'N/A';
    }
};

export const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
    }).format(date);
};

export const formatDateNumeric = (dateString: string | null) => {
    if (!dateString) return '';
    try {
        return format(parseISO(dateString), 'MM/dd/yyyy');
    } catch {
        return dateString;
    }
};

/**
 * Formats a number as currency with proper symbol and formatting
 * Example: "$1,234.56"
 *
 * @param amount Number value to format as currency
 * @param currencyCode Currency code (default: 'USD')
 * @param options Optional Intl.NumberFormatOptions to customize the format
 * @returns Formatted currency string or '$0.00' if invalid
 */
export const formatCurrency = (
    amount: number | string | null | undefined,
    currencyCode: string = 'USD',
    options: Intl.NumberFormatOptions = {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }
): string => {
    // Return 'N/A' for null or undefined
    if (amount === null || amount === undefined) return '$0.00';

    // Store the original input to return on failure
    const originalInput = typeof amount === 'string' ? amount : String(amount);

    try {
        // Convert to number if it's a string
        let numericAmount: number;

        if (typeof amount === 'string') {
            // Remove currency symbols, commas and spaces
            const cleanedAmount = amount.replace(/[$,\s]/g, '');
            numericAmount = parseFloat(cleanedAmount);
        } else {
            numericAmount = amount;
        }

        // Check if numeric amount is valid
        if (isNaN(numericAmount)) {
            console.warn('Invalid currency amount:', amount);
            return originalInput;
        }

        // Check for zero value
        if (numericAmount === 0) {
            return '$0.00';
        }

        // Set the currency in options if provided
        const formatOptions = { ...options, currency: currencyCode };

        // Format the currency using Intl.NumberFormat for localization support
        return new Intl.NumberFormat('en-US', formatOptions).format(numericAmount);
    } catch (error) {
        console.error('Error formatting currency:', error);
        return originalInput;
    }
};